import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table } from '@roo/roo';

import './list.scss';
import { LineChart } from '@roo/roo-chart';
import { Icon } from 'RooV1.12';
import { OnlineUsersPointData, getLiveData, getOnlineUsers } from './api';
import url from '@/utils/url';

interface IProps {
    liveId: number;
}

const defaultStatisticsData: urls.live.ILiveData = {
    behavior: {
        liveId: 0,
        avgPoiViewTime: '12:00',
        viewPv: 0,
        viewUv: 0,
        liveCommentsNum: 0,
    },
    orders: [],
};

export default function (props: IProps) {
    const chartRef = useRef<LineChart<any>>(null);
    const { liveId } = props;
    const [chartData, setChartData] = useState<OnlineUsersPointData[]>([]);
    const [statisticsData, setStatisticsData] =
        useState<urls.live.ILiveData>(defaultStatisticsData);
    const columns = [
        {
            label: '商品id',
            prop: 'spuId',
            // width: 200,
        },
        {
            label: '商品名称',
            prop: 'spuName',
            // width: 200,
        },
        {
            label: '预告订单数',
            prop: 'trailerOrderCount',
            render: num => formatNumberWithComma(num),
        },
        {
            label: '预告实付交易额(元)',
            prop: 'trailerOrderPayTotal',
            render: num => formatNumberWithComma(num),
        },
        {
            label: '直播间订单数',
            prop: 'liveOrdNum',
            render: num => formatNumberWithComma(num),
        },
        {
            label: '直播间实付交易额(元)',
            prop: 'liveActualAmt',
            render: num => formatNumberWithComma(num),
        },
        {
            label: '回放订单数',
            prop: 'playbackOrdNum',
            render: num => formatNumberWithComma(num),
        },
        {
            label: '回放实付交易额(元)',
            prop: 'playbackActualAmt',
            render: num => formatNumberWithComma(num),
        },
    ];

    // 提取时间 '2024-05-23 16:02:00' => '16:02'
    const extractTime = (dateTimeString: string) => {
        const match = dateTimeString.match(/\d{4}-\d{2}-\d{2}\s(\d{2}:\d{2}):\d{2}/);
        return match ? match[1] : '';
    };

    const getChartData = () => {
        getOnlineUsers({
            busId: url.commonParams.providerId,
            busLine: 1,
            liveId,
        })
            .then(res => {
                res.data.forEach(item => {
                    item.time = extractTime(item.time);
                    item['同时在线人数'] = item.onlineUser;
                });
                setChartData(res.data);
            })
            .catch(() => { });
    };

    /** 获取统计数据 */
    const getStatisticsData = () => {
        getLiveData({
            busId: url.commonParams.providerId,
            busLine: 1,
            liveId,
        })
            .then(res => {
                setStatisticsData(res.data);
            })
            .catch(() => { });
    };

    /** 使用逗号分隔3位数字 */
    const formatNumberWithComma = (num: number | string) => {
        if (num === undefined || num === null || num === '') {
            return '--';
        }
        // 将数字转换为字符串，并分割整数部分和小数部分
        const parts = num.toString().split('.');
        // 对整数部分使用正则表达式进行逗号分隔
        const reg = /\B(?=(\d{3})+(?!\d))/g;
        parts[0] = parts[0].replace(reg, ',');
        // 将处理后的整数部分和小数部分重新组合
        return parts.join('.');
    };

    const maxOnlineCount = useMemo(() => {
        if (!chartData.length) {
            return '';
        }
        let max = 0;
        chartData.forEach(item => {
            max = Math.max(max, item.onlineUser);
        });
        return max;
    }, [chartData]);

    useEffect(() => {
        getChartData();
        getStatisticsData();
    }, []);

    useEffect(() => {
        if (chartData.length > 0 && (chartRef.current as any)?.chart?.g2Chart) {
            (chartRef.current as any)?.chart?.g2Chart.scale('同时在线人数', {
                minTickInterval: 1,
            });
        }
    }, [chartData]);

    return (
        <div className="show-wrapper">
            <div className="data-title">1.直播间互动数据：</div>
            <div className="data-item active-data">
                <div className="item-chart">
                    {chartData.length > 0 ? (
                        <LineChart
                            ref={chartRef}
                            xAxis={{ field: 'time', tickCount: 10 }}
                            yAxis={{ field: '同时在线人数' }}
                            chart={{ height: 450, padding: 60 }}
                            series={chartData}
                            key={JSON.stringify(chartData)}
                            point={false}
                            title={{
                                title: '直播间同时在线人数图',
                                style: {
                                    color: '#333',
                                    fontSize: 15,
                                    textAlign: 'center',
                                    marginBottom: 0,
                                },
                                subStyle: { display: 'none' },
                            }}
                        />
                    ) : (
                        <div className="none-data">目前暂无数据，数据产生后将刷新</div>
                    )}
                </div>
                <ul className="item-statistics">
                    <li>
                        商家平均停留时长（分）：
                        {formatNumberWithComma(statisticsData.behavior?.avgPoiViewTime)}
                    </li>
                    <li>
                        累计观看人次（次）：
                        {formatNumberWithComma(statisticsData.behavior?.viewPv)}
                    </li>
                    <li>
                        累计观看人数（人）：
                        {formatNumberWithComma(statisticsData.behavior?.viewUv)}
                    </li>
                    <li>
                        最高同时在线人数（人）：
                        {formatNumberWithComma(maxOnlineCount)}
                    </li>
                    <li>
                        直播间评论（条）：
                        {formatNumberWithComma(statisticsData.behavior?.liveCommentsNum)}
                    </li>
                    <p style={{ marginTop: '15px', color: '#84ceea' }}>
                        <Icon name="info-circle" />
                        除在线人数外，其余数据均T+1展示
                    </p>
                </ul>
            </div>
            <div className="data-title">2.直播间交易数据：</div>
            <div className="data-item">
                <div className="item-content">
                    <Table
                        border
                        columns={columns}
                        data={statisticsData.orders || []}
                        emptyComponent="暂无数据"
                    />
                </div>
            </div>
        </div>
    );
}
